import React, { useState } from 'react';
import { Settings, Save, Filter, Columns, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { FilterConfig } from './InlineFilters';
import { Column } from '@/components/ui/data-table';

export interface FilterPreset {
    id: string;
    name: string;
    description?: string;
    filters: FilterConfig;
    isDefault?: boolean;
}

interface TableSettingsMenuProps {
    // Filter preset related
    filters: FilterConfig;
    presets: FilterPreset[];
    onLoadPreset: (preset: FilterPreset) => void;
    onSavePreset?: (preset: Omit<FilterPreset, 'id'>) => void;

    // Column settings related
    columns: Column[];
    visibleColumns: string[];
    onColumnToggle: (columnId: string) => void;
    onResetColumns: () => void;

    // Export functionality
    onExport?: () => void;
    isExporting?: boolean;
}

const TableSettingsMenu: React.FC<TableSettingsMenuProps> = ({
    filters,
    presets,
    onLoadPreset,
    onSavePreset,
    columns,
    visibleColumns,
    onColumnToggle,
    onResetColumns,
    onExport,
    isExporting = false,
}) => {
    const [showSavePresetDialog, setShowSavePresetDialog] = useState(false);
    const [showColumnDialog, setShowColumnDialog] = useState(false);
    const [presetName, setPresetName] = useState('');
    const [presetDescription, setPresetDescription] = useState('');

    const availableColumns = columns.filter(col => col.id !== 'selection' && col.id !== 'actions');
    const hasActiveFilters = Object.keys(filters).some(key => {
        const value = filters[key];
        return value !== '' && value !== null && value !== undefined;
    });

    const handleSavePreset = () => {
        if (presetName.trim() && onSavePreset) {
            onSavePreset({
                name: presetName.trim(),
                description: presetDescription.trim(),
                filters: filters,
            });
            setPresetName('');
            setPresetDescription('');
            setShowSavePresetDialog(false);
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Settings
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Table Settings</DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {/* Save Current Filter */}
                    {hasActiveFilters && onSavePreset && (
                        <DropdownMenuItem
                            onClick={() => setShowSavePresetDialog(true)}
                            className="flex items-center gap-2"
                        >
                            <Save className="h-4 w-4" />
                            Save Current Filter
                        </DropdownMenuItem>
                    )}

                    {/* Export */}
                    {onExport && typeof onExport === 'function' && (
                        <DropdownMenuItem
                            onClick={isExporting ? undefined : onExport}
                            disabled={isExporting}
                            className="flex items-center gap-2"
                        >
                            {isExporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Download className="h-4 w-4" />
                            )}
                            {isExporting ? 'Exporting...' : 'Export Data'}
                        </DropdownMenuItem>
                    )}

                    {/* Columns */}
                    <DropdownMenuItem
                        onClick={() => setShowColumnDialog(true)}
                        className="flex items-center gap-2"
                    >
                        <Columns className="h-4 w-4" />
                        Manage Columns
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            {/* Save Filter Preset Dialog */}
            <Dialog open={showSavePresetDialog} onOpenChange={setShowSavePresetDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Save Filter Preset</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex flex-col space-y-2">
                            <Label htmlFor="preset-name">Preset Name</Label>
                            <Input
                                id="preset-name"
                                placeholder="Enter preset name..."
                                value={presetName}
                                onChange={e => setPresetName(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col space-y-2">
                            <Label htmlFor="preset-description">Description (optional)</Label>
                            <Textarea
                                id="preset-description"
                                placeholder="Enter preset description..."
                                value={presetDescription}
                                onChange={e => setPresetDescription(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSavePresetDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSavePreset} disabled={!presetName.trim()}>
                            Save Preset
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Column Settings Dialog */}
            <Dialog open={showColumnDialog} onOpenChange={setShowColumnDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Customize Columns</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex flex-col space-y-2">
                            <Label>Select columns to display:</Label>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {availableColumns.map(column => (
                                    <div key={column.id} className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`crm-table-col-${column.id}`}
                                            checked={visibleColumns.includes(column.id)}
                                            onCheckedChange={() => onColumnToggle(column.id)}
                                        />
                                        <Label
                                            htmlFor={`crm-table-col-${column.id}`}
                                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                        >
                                            {column.label}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={onResetColumns}>
                            Reset to Default
                        </Button>
                        <Button onClick={() => setShowColumnDialog(false)}>Apply</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TableSettingsMenu;
